import { Model } from "@/models/base/model";
import { validationCommon, ValidationResult } from "@/utils/validation-common";

export class DiagnosisProcess extends Model {
  private diagnosisProcessUrl = "/cms/api/my-record/diagnosis-process";
  private dpfUrl = "/cms/api/dpf";

  // コンストラクタ
  constructor(params: any = {}) {
    super(params);
    this.url = this.diagnosisProcessUrl;
    this.validation = this.getValidationSchema();
    this.labels = this.getValidationLabels();
  }

  // 経緯画面情報取得
  async fetchIndexData(): Promise<any> {
    this.url = `${this.diagnosisProcessUrl}/index`;
    return await super.fetch();
  }

  // 経緯データ登録・更新
  async save(data: any): Promise<any> {
    this.url = `${this.diagnosisProcessUrl}/save`;
    return await super.save(data, "POST");
  }

  // 都道府県一覧取得（DPF API）
  async fetchPrefecture(): Promise<any> {
    this.url = `${this.dpfUrl}/get-prefectures`;
    return await super.fetch();
  }

  // 市区町村取得（都道府県コードから）
  async fetchCity(prefectureCode: string): Promise<any> {
    this.url = `${this.dpfUrl}/get-municipalities?prefCode=${encodeURIComponent(
      prefectureCode
    )}`;
    return await super.fetch();
  }

  // 独自のバリデーション処理
  validate_addon(): ValidationResult {
    const errors = [];
    // 独自チェックを持つ項目リスト作成
    const list = validationCommon.getAddonList(this.validation);

    // 各項目でチェック処理を行う
    for (const obj of list) {
      // hasValueチェック
      if (obj.value.addOn.hasValue !== undefined) {
        // initialSymptomsの場合、症状1（initialSymptoms[0].symptom）のみ必須
        if (obj.key === "initialSymptoms") {
          const initialSymptoms = this.attributes[obj.key];
          if (
            !initialSymptoms ||
            !Array.isArray(initialSymptoms) ||
            !initialSymptoms[0] ||
            !validationCommon.hasValue(initialSymptoms[0].symptom)
          ) {
            const msg = `${this.labels[obj.key]}は必須です`;
            errors.push({
              key: obj.key,
              value: initialSymptoms,
              validation_type: "hasValue",
              msg: msg,
            });
          }
        }
        // diagnosisYearの場合、medicalCheckupStatus === 1かつdiagnosisYearが存在する（undefinedでない）場合のみ必須チェック
        else if (obj.key === "diagnosisYear") {
          const value = this.attributes[obj.key];
          // medicalCheckupStatus === 1（受けている）かつvalueがundefinedでない（Step2まで進んでいる）場合のみ必須エラー
          if (
            this.attributes.medicalCheckupStatus === 1 &&
            value !== undefined &&
            !validationCommon.hasValue(value)
          ) {
            const msg = `${this.labels[obj.key]}は必須です`;
            errors.push({
              key: obj.key,
              value: value,
              validation_type: "hasValue",
              msg: msg,
            });
          }
        }
        // その他の項目（initialSymptomYearなど）
        else {
          const result = validationCommon.hasValue(this.attributes[obj.key]);
          if (!result) {
            const msg = `${this.labels[obj.key]}は必須です`;
            errors.push({
              key: obj.key,
              value: this.attributes[obj.key],
              validation_type: "hasValue",
              msg: msg,
            });
          }
        }
      }
    }

    const valid = errors.length === 0;
    // 返却情報の作成
    return validationCommon.createResult(valid, errors);
  }

  // バリデーションルール
  getValidationSchema() {
    return {
      // ===== Step1 =====
      // 初期症状の発症時期（年）※必須
      initialSymptomYear: {
        required: true,
        type: "string",
        addOn: {
          hasValue: true,
        },
      },
      // 初期症状の発症時期（月）※任意
      initialSymptomMonth: {
        type: "string",
      },
      // 初期症状の発症時期（日）※任意
      initialSymptomDay: {
        type: "string",
      },
      // 初期症状（最初の症状のみ必須）
      initialSymptoms: {
        required: true,
        type: "array",
        maxItems: 20,
        addOn: {
          hasValue: true, // 最初の症状（initialSymptoms[0].symptom）が必須
        },
      },
      // 確定診断受診ステータス※必須（0: 受けていない, 1: 受けている）
      medicalCheckupStatus: {
        type: "number",
        addOn: {
          hasValue: true,
        },
      },
      // ===== Step2 =====
      // 確定診断の時期（年）※medicalCheckupStatus === 1の場合のみ必須
      diagnosisYear: {
        type: "string",
        addOn: {
          hasValue: true, // 条件付き必須
        },
      },
      // 確定診断の時期（月）※任意
      diagnosisMonth: {
        type: "string",
      },
      // 確定診断の時期（日）※任意
      diagnosisDay: {
        type: "string",
      },
      // 確定診断までの病院訪問数（汎用マスタID）※任意
      hospitalVisitsCountRangeOptionId: {
        type: "string",
      },
      // 確定診断先の都道府県※任意
      finalDiagPref: {
        type: "string",
      },
      // 確定診断先の市区郡※任意
      finalDiagCity: {
        type: "string",
      },
      // 紹介元の都道府県※任意
      refSourcePref: {
        type: "string",
      },
      // 紹介元の市区郡※任意
      refSourceCity: {
        type: "string",
      },
    };
  }
  // バリデーションラベル
  getValidationLabels() {
    return {
      initialSymptomYear: "年",
      initialSymptomMonth: "月",
      initialSymptomDay: "日",
      initialSymptoms: "症状1",
      medicalCheckupStatus: "確定診断の有無",
      diagnosisYear: "年",
      diagnosisMonth: "月",
      diagnosisDay: "日",
      hospitalVisitsCountRangeOptionId: "確定診断までの病院訪問数",
      finalDiagPref: "都道府県",
      finalDiagCity: "市区郡",
      refSourcePref: "都道府県",
      refSourceCity: "市区郡",
    };
  }
}
